import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from "recharts";
import { displayLabel } from "../../utils/format";

const DONUT_COLORS = ["#32B1FF", "#2DD4BF", "#34D399", "#A78BFA", "#9CA3AF"];

const RvIntelligencePanel = ({ rvTypes, rvLengths, equipment, slideOuts, loading }) => {
  const hasAnyData = rvTypes?.length || rvLengths?.length || equipment?.length || slideOuts?.length;

  if (loading) {
    return (
      <div className="animate-pulse grid grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 bg-gray-200 dark:bg-gray-800 rounded" />
        ))}
      </div>
    );
  }

  if (!hasAnyData) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        No RV data for this period.
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-lg text-sm">
        <div className="font-semibold text-gray-900 dark:text-gray-100">{d?.name || d?.rv_type || d?.length_bucket}</div>
        <div className="text-gray-600 dark:text-gray-400">{d?.sessions || d?.value} sessions</div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* A: RV Type Distribution */}
      <div>
        <h4 className="text-sm font-semibold text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">RV Type</h4>
        {rvTypes?.length ? (
          <div className="space-y-1.5">
            {rvTypes.map((r) => {
              const maxSess = rvTypes[0]?.sessions || 1;
              const rvLabel = displayLabel(r.rv_type);
              return (
                <div key={r.rv_type}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]" title={rvLabel}>{rvLabel}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{r.sessions} / {r.bookings} · {r.conversion_pct}%</span>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-3 overflow-hidden">
                    <div className="h-full rounded-full bg-[#32B1FF]/30 transition-all" style={{ width: `${(r.sessions / maxSess) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">No data</div>
        )}
      </div>

      {/* B: Length Distribution */}
      <div>
        <h4 className="text-sm font-semibold text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">RV Length</h4>
        {rvLengths?.length ? (
          <ResponsiveContainer width="100%" height={130}>
            {/* 2026-07-29 — RPC returns `bucket` (with `bucket_order` for
                sort). Old code read `length_bucket` which never existed, so
                the chart rendered the y-axis but zero bars. Alias the field
                on the way in so Recharts can find it, and pre-sort by the
                RPC's bucket_order so 40ft+ doesn't land next to Under 25ft. */}
            <BarChart
              data={[...rvLengths].sort((a, b) => (a.bucket_order ?? 99) - (b.bucket_order ?? 99)).map((r) => ({ ...r, length_bucket: r.bucket ?? r.length_bucket }))}
              margin={{ top: 15, right: 0, left: -20, bottom: 0 }}
            >
              <XAxis dataKey="length_bucket" tick={{ fontSize: 9, fill: "#9CA3AF" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#9CA3AF" }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="sessions" fill="rgba(50,177,255,0.35)" radius={[3, 3, 0, 0]}>
                <LabelList dataKey="sessions" position="top" fill="#9CA3AF" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">No data</div>
        )}
      </div>

      {/* C: Top Equipment */}
      <div>
        <h4 className="text-sm font-semibold text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">Top Equipment</h4>
        {equipment?.length ? (
          <div className="space-y-1">
            {equipment.map((e, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-sm text-gray-400 dark:text-gray-500 w-4 shrink-0">{i + 1}.</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 truncate">
                  {e.make}{e.model ? ` ${e.model}` : ""}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                  {e.sessions} / {e.bookings}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">No data</div>
        )}
      </div>

      {/* D: Slide-Outs */}
      <div>
        <h4 className="text-sm font-semibold text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">Slide-Outs</h4>
        {(() => {
          // 2026-07-29 — the RPC (`fn_get_rv_intelligence`) returns rows with
          // the field name `position`, NOT `slide_outs`. Reading `s.slide_outs`
          // gave `undefined` for every row → displayLabel(undefined, 'Unknown')
          // → every slide-out rendered as "Unknown" in the pie legend. Also
          // the "Not specified" filter didn't match the RPC's actual "None"
          // value, so meaningful/total math was off. Normalize once and use
          // `label` throughout.
          const rows = (slideOuts || []).map((s) => ({
            label: s.position ?? s.slide_outs ?? null,
            sessions: Number(s.sessions) || 0,
            bookings: Number(s.bookings) || 0,
          }));
          // "None" is a valid meaningful answer (RV has no slide-outs); only
          // legacy sentinel values collapse to "no data".
          const meaningful = rows.filter((r) => r.label && !['Not specified', 'undefined', 'Unknown'].includes(r.label));
          const total = rows.reduce((sum, r) => sum + r.sessions, 0);
          const meaningfulTotal = meaningful.reduce((sum, r) => sum + r.sessions, 0);

          if (!rows.length || meaningfulTotal < total * 0.05) {
            return (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-2 leading-relaxed">
                Slide-out data becomes available when guests specify RV details during checkout.
                {total > 0 && <> {meaningfulTotal} of {total} RV sessions included this information.</>}
              </div>
            );
          }

          if (meaningful.length === 1) {
            return (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                {displayLabel(meaningful[0].label)}: {meaningful[0].sessions} sessions
              </div>
            );
          }

          return (
            <div>
              <ResponsiveContainer width="100%" height={100}>
                <PieChart>
                  <Pie
                    data={meaningful.map((r) => ({ name: displayLabel(r.label), value: r.sessions }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={25}
                    outerRadius={40}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {meaningful.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-1 justify-center">
                {meaningful.map((r, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{displayLabel(r.label)} ({r.sessions})</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default RvIntelligencePanel;
